import type { ApolloQueryResult, ApolloError } from "@apollo/client";
import { useMemo } from "react";
import { Platform } from "react-native";

import { useViewjobProctorAllocation } from "../../proctor/__generated__/ViewjobProctorContext";
import {
  RnEmployerOutreachTst,
  RnViewjobOnegraphTst,
} from "../../proctor/__generated__/ViewjobProctorSpecifications";
import {
  type FindResumeEngagementQuery,
  useFindResumeEngagementQuery,
} from "../components/core/JobComponent/EmployerOutreach/__generated__/findResumeEngagement.gqlgenerated";
import { useFullScreenVJActive } from "./useFullScreenVJActive";

export interface ResumeEngagementContextData {
  resumeEngagementData: FindResumeEngagementQuery | undefined;
  resumeEngagementLoading: boolean;
  resumeEngagementError: ApolloError | undefined;
  refetchResumeEngagement: () => Promise<ApolloQueryResult<FindResumeEngagementQuery>>;
  shouldQueryResumeEngagement: boolean;
}

/**
 * Custom hook to handle resume engagement query with proctor-based conditional logic
 * @returns Object containing resume engagement data, loading state, error, and refetch function
 */
export const useFindResumeEngagement = () => {
  // Proctor checks for resume engagement query
  const isEmployerOutreachActive =
    useViewjobProctorAllocation(RnEmployerOutreachTst).value > 1 || Platform.OS === "web";
  const isViewjobOneGraphActive = useViewjobProctorAllocation(RnViewjobOnegraphTst).value > 1;
  const isFullScreenVJActive = useFullScreenVJActive();
  const shouldQueryResumeEngagement =
    (isFullScreenVJActive || isEmployerOutreachActive) && isViewjobOneGraphActive;

  // Calculate 30 days ago for resume engagement query.
  // Memoized with no deps so it is computed once per mount and does not
  // change on subsequent re-renders, preventing spurious Apollo re-fetches.
  const startTime = useMemo(() => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return thirtyDaysAgo.toISOString().slice(0, 19) + "Z";
  }, []);

  const {
    data: resumeEngagementData,
    loading: resumeEngagementLoading,
    error: resumeEngagementError,
    refetch: refetchResumeEngagement,
  } = useFindResumeEngagementQuery({
    variables: {
      resumeEngagementsInput: {
        startTime,
      },
    },
    skip: !shouldQueryResumeEngagement,
  });

  return {
    resumeEngagementData,
    resumeEngagementLoading,
    resumeEngagementError,
    refetchResumeEngagement,
    shouldQueryResumeEngagement,
  };
};
